/**
 * The one definition of an order's cost breakdown, shared by checkout (which
 * prices the order) and every screen that displays a placed order.
 *
 *   total = subTotal + serviceFee + deliveryFee + tax − discount − walletCredit
 *
 * All amounts are whole KES. mzito-pay (src/utils/orderCost.ts) recomputes the
 * same breakdown server-side to decide what M-Pesa actually charges, and mzito
 * stores it as-is — keep the three in step.
 */

export const SERVICE_FEE_RATE = 0.1; // 10% of subtotal

/** Rounds to whole shillings — M-Pesa and the stored order both deal in whole KES. */
export function roundKes(amount) {
  const value = Number(amount);
  return Number.isFinite(value) ? Math.round(value) : 0;
}

/** @param {number} subTotal */
export function calculateServiceFee(subTotal) {
  return subTotal > 0 ? roundKes(subTotal * SERVICE_FEE_RATE) : 0;
}

/**
 * @param {{ subTotal?: number, serviceFee?: number, deliveryFee?: number, tax?: number, discount?: number, walletCredit?: number }} cost
 * @returns {number}
 */
export function calculateOrderTotal(cost = {}) {
  return (
    roundKes(cost.subTotal) +
    roundKes(cost.serviceFee) +
    roundKes(cost.deliveryFee) +
    roundKes(cost.tax) -
    roundKes(cost.discount) -
    roundKes(cost.walletCredit)
  );
}

/**
 * Normalizes a stored order's `cost` for display. The stored `total` is what
 * the customer was actually charged, so it always wins. Orders placed before
 * serviceFee/discount were persisted only have subTotal/deliveryFee/tax/total;
 * for those, whatever's left over is attributed to the service fee (if
 * positive) or a discount (if negative) so the lines still add up to the total.
 *
 * @param {object} cost - order.cost as returned by the order history API
 */
export function normalizeOrderCost(cost = {}) {
  const subTotal = roundKes(cost?.subTotal);
  const deliveryFee = roundKes(cost?.deliveryFee);
  const tax = roundKes(cost?.tax);
  const walletCredit = roundKes(cost?.walletCredit);
  let serviceFee = roundKes(cost?.serviceFee);
  let discount = roundKes(cost?.discount);

  if (cost?.serviceFee == null && cost?.total != null) {
    const residual = roundKes(cost.total) - (subTotal + deliveryFee + tax - discount - walletCredit);
    serviceFee = Math.max(0, residual);
    discount += Math.max(0, -residual);
  }

  const breakdown = { subTotal, serviceFee, deliveryFee, tax, discount, walletCredit };
  const total = cost?.total != null ? roundKes(cost.total) : calculateOrderTotal(breakdown);
  return { ...breakdown, total };
}

/**
 * Display lines for an order's cost breakdown, in the same order and with the
 * same labels everywhere (order history, admin views, confirmation email,
 * Telegram). Tax/discount/wallet lines only appear when non-zero; amounts
 * that reduce the total are negative.
 *
 * @returns {{ label: string, value: number }[]}
 */
export function getOrderCostLines(cost) {
  const c = normalizeOrderCost(cost);
  return [
    { label: "Subtotal", value: c.subTotal },
    { label: "Service Fee", value: c.serviceFee },
    { label: "Delivery", value: c.deliveryFee },
    c.tax > 0 && { label: "Tax", value: c.tax },
    c.discount > 0 && { label: "Discount", value: -c.discount },
    c.walletCredit > 0 && { label: "Wallet Credit", value: -c.walletCredit },
  ].filter(Boolean);
}
